import { watchDebounced } from '@vueuse/core'
import type { UserRegister } from './interface'
import { AUTH_API, PROFILE_API } from '~/configs/api'
import { notificationPopup } from '~utils/modal/notify'
import { COOKIE_MAX_AGE_DAYS, SECONDS_PER_DAY } from '~/constants'
import { useApi } from '~/composables/common/use-api'
import { useCurrentUserStore } from '~/stores/user-store.store.ts'
import { useError } from '~/composables/common/use-error'
import { usePromotion } from '~/composables/promotion/use-promotion'
import { useUpdateProfile } from '~composables/account/use-update-profile'
import { modalList } from '~resources/popup-notification'
import { useModal } from '~/composables/common/use-modal'
import { ResponseStatus } from '~composables/common/interface'
import { useRegisterAddonStore } from '~/stores/user-register-addon.store.ts'
import { useModalWelcome } from '~/composables/event/use-modal-welcome'
import { EVENT } from '~/configs/page-url'
import { useEventEntryHalloween } from '~/composables/events/halloween/use-event-entry'
declare global {
  interface Window {
    referrerMKT: {
      getRefDomain(): string
    }
  }
}

export function useRegister() {
  const gtm = useGtm()
  const config = useRuntimeConfig()
  const { request } = useApi()
  const store = useCurrentUserStore()
  const { checkDisplayNameExist } = useUpdateProfile()
  const router = useRouter()
  const { showError } = useError()
  const { setPromotion, getPromotionPackages } = usePromotion()
  const { closeAllModal } = useModal()
  const { openModalEvent } = useModalWelcome()
  const route = useRoute()
  const { openModalEventHalloWeen } = useEventEntryHalloween()

  const messageError = ref('')
  const messageUsernameExist = ref('')
  const messageDisplayNameExist = ref('')
  const user = reactive<UserRegister>({
    username: '',
    displayName: '',
    password: '',
    passwordConfirm: '',
    phone: ''
  })
  function resetError(): void {
    messageError.value = ''
  }
  function resetUsernameError(): void {
    messageError.value = ''
    messageUsernameExist.value = ''
  }
  function resetDisplaynameError(): void {
    messageError.value = ''
    messageDisplayNameExist.value = ''
  }

  async function checkUsernameExist(username: string) {
    try {
      const { data, status } = await request(PROFILE_API.CHECK_USERNAME, { method: 'POST', body: { username } })
      if (status === ResponseStatus.OK) {
        return !!data.exists
      }
      return true
    } catch {
      return true
    }
  }

  async function handleSubmitRegister(): Promise<void> {
    messageError.value = ''

    try {
      const { queries } = useRegisterAddonStore()
      const source = window.referrerMKT.getRefDomain()
      const { data, status, message } = await request(AUTH_API.REGISTER, {
        method: 'POST',
        body: {
          source,
          username: user.username,
          password: user.password,
          confirmPassword: user.passwordConfirm,
          fullname: user.displayName,
          phoneNumber: user.phone,
          queries
        }
      })

      if (data && status === ResponseStatus.OK) {
        const numberOfAvatar = 11
        // register success
        store.setCurrentUser({
          ...data,
          hasBank: false,
          avatar: (data.login_count % numberOfAvatar) + 1
        })
        useCookie('token', { maxAge: SECONDS_PER_DAY * COOKIE_MAX_AGE_DAYS }).value = store.data.token
        void setPromotion()
        void getPromotionPackages()
        gtm?.trackEvent({ event: 'formSubmitted', formName: 'Form_Register' })
        closeAllModal()
        notificationPopup(modalList.REGISTER_SUCCESS)

        if (route.path === EVENT.PREMIER_LEAGUE_RANKING || route.path === EVENT.HALLOWEEN) return

        void router.push('/')

        if (config.public.EVENT_PREMIER_LEAGUE_RANKING_ENABLED) {
          openModalEvent()
        }

        if (config.public.EVENT_HALLOWEEN_ENABLE) {
          openModalEventHalloWeen()
        }
      } else {
        switch (status) {
          case ResponseStatus.USER_EXISTED_ERROR:
            messageUsernameExist.value = message ?? 'Tài khoản này đã tồn tại'
            break
          case ResponseStatus.EXISTED_DISPLAYNAME_ERROR:
            messageDisplayNameExist.value = message ?? 'Tên hiển thị này đã tồn tại'
            break
          default:
            messageError.value = message ?? 'Đã xảy ra lỗi. Vui lòng kiểm tra lại!'
            break
        }
      }
    } catch {
      messageError.value = 'Đã xảy ra lỗi. Vui lòng kiểm tra lại!'
    }
    // Show error
    if (messageError.value) {
      showError(messageError.value)
    }
  }

  watchDebounced(
    () => user.username,
    async newUsername => {
      if (await checkUsernameExist(newUsername)) {
        messageUsernameExist.value = 'Tài khoản này đã tồn tại'
      }
    },
    { debounce: 500 }
  )

  const checkDisplayName = async (newDisplayName: string) => {
    if (!newDisplayName) {
      return
    }
    if (await checkDisplayNameExist(newDisplayName)) {
      messageDisplayNameExist.value = 'Tên hiển thị này đã tồn tại'
    }
  }

  return {
    user,
    messageError,
    messageUsernameExist,
    messageDisplayNameExist,
    handleSubmitRegister,
    resetError,
    resetUsernameError,
    resetDisplaynameError,
    checkDisplayName
  }
}
